import { useCallback, useMemo, useRef, useState } from 'react';

import type { IconButtonSize } from 'components/IconButton';
import {
	useAnimator,
	useDebouncedCallback,
	usePrefersReducedMotion,
	useThrottledCallback,
} from 'hooks';
import type { ExtractStrict } from 'types';
import { cn } from 'utils/classNames';
import { is } from 'utils/helpers';
import { clamp, clampIndex, round } from 'utils/math';

export type AnimationDuration = 'fast' | 'medium' | 'none' | 'slow';
export type ControlsSize = ExtractStrict<IconButtonSize, 'md' | 'sm' | 'xs'>;
export type Direction = 'next' | 'prev';
// TODO: the responsive version feels a bit awkward, think about alternatives.
export type Gutter = 'focus-outline' | 'max-sm:md' | 'md' | 'none';
export type Orientation = 'horizontal' | 'vertical';
export type SnapAlignment = 'center' | 'edge';

interface CarouselData {
	containerSize: number;
	controlButtonSize: number;
	gapSize: number;
	gutterSize: number;
	snapAlignment: SnapAlignment;
	totalSize: number;
}

export interface ItemData {
	index: number;
	isFullyVisible: boolean;
	isPartiallyVisible: boolean;
	isVisible: boolean;
	pos: number;
	posEnd: number;
	size: number;
	visiblePercentage: number;
}

export const ANIMATION_DURATIONS: Record<
	AnimationDuration,
	{ className: string; time: number }
> = {
	fast: { className: 'duration-150', time: 150 },
	medium: { className: 'duration-300', time: 300 },
	none: { className: 'duration-0', time: 0 },
	slow: { className: 'duration-450', time: 450 },
};

// Edge spacing for inline buttons must be set with padding (margin on the
// first/last item or an empty dummy item both break grid sizing). Padding
// is also used for an outer gutter so just reading the padding won't say
// how much each is using. Reading the custom property returns the raw rem
// value so it must be set as an existing CSS property.
// Using a property that is the least likely to affect layout no matter what
// classes might be set via props.
// An alternative could be transform-origin if there is ever a compat issue
// with border-spacing, but that's a double value and requires more parsing.
export const INLINE_CONTROLS_SIZE_PROP = 'borderSpacing';
export function getInlineControlsSizeClass(controlsSize: ControlsSize) {
	if (controlsSize === 'xs') {
		return '[--control-size:2.25rem] [border-spacing:2.25rem]';
	}
	return '[--control-size:3.5rem] [border-spacing:3.5rem]';
}

// TODO: These gutter helpers feel off. Would usually be done inline but the
// many checks make it messy. Also want to keep things together since they
// all use the same values like r-md but in different ways.
type GutterClassMap = Record<Gutter, string | undefined>;
interface GutterClassGetterArgs {
	gutter: Gutter;
	isVertical: boolean;
}

export function getGutterPaddingClass({
	gutter,
	isVertical,
}: GutterClassGetterArgs) {
	// Must use padding for both general gutter and reserved space for inline
	// control buttons, see --control-size definition above.
	const classes: GutterClassMap = {
		'none': isVertical ? 'py-(--control-size,0px)' : 'px-(--control-size,0px)',
		'focus-outline': isVertical
			? 'py-[calc(var(--spacing-focus-outline-size)+var(--control-size,0px))]'
			: 'px-[calc(var(--spacing-focus-outline-size)+var(--control-size,0px))]',
		'md': isVertical
			? 'py-[calc(var(--spacing-r-md)+var(--control-size,0px))]'
			: 'px-[calc(var(--spacing-r-md)+var(--control-size,0px))]',
		'max-sm:md': isVertical
			? 'max-sm:py-[calc(var(--spacing-r-md)+var(--control-size,0px))]'
			: 'max-sm:px-[calc(var(--spacing-r-md)+var(--control-size,0px))]',
	};
	return cn(
		classes[gutter],
		gutter !== 'none' && [
			// Pad in the non-gutter direction to ensure focus outline is visible.
			isVertical ? 'px-focus-outline-size' : 'py-focus-outline-size',
		],
	);
}

export function getGutterMarginPullClass({
	gutter,
	isVertical,
}: GutterClassGetterArgs) {
	const classes: GutterClassMap = {
		'none': undefined,
		'focus-outline': isVertical
			? '-my-focus-outline-size'
			: '-mx-focus-outline-size',
		'md': isVertical ? '-my-r-md' : '-mx-r-md',
		'max-sm:md': isVertical ? 'max-sm:-my-r-md' : 'max-sm:-mx-r-md',
	};
	return cn(
		classes[gutter],
		gutter !== 'none' && [
			// Pull in the non-gutter direction to match gutter padding above.
			isVertical ? '-mx-focus-outline-size' : '-my-focus-outline-size',
		],
	);
}

export function getGutterScrollPaddingClass({
	gutter,
	isVertical,
}: GutterClassGetterArgs) {
	const classes: GutterClassMap = {
		'none': undefined,
		'focus-outline': isVertical
			? 'scroll-py-focus-outline-size'
			: 'scroll-px-focus-outline-size',
		'md': isVertical ? 'scroll-py-r-md' : 'scroll-px-r-md',
		'max-sm:md': isVertical ? 'max-sm:scroll-py-r-md' : 'max-sm:scroll-px-r-md',
	};
	return classes[gutter];
}

export function getInlinePrevButtonMarginClass({
	gutter,
	isVertical,
}: GutterClassGetterArgs) {
	const classes: GutterClassMap = {
		'none': undefined,
		'focus-outline': isVertical
			? 'mt-focus-outline-size'
			: 'ml-focus-outline-size',
		'md': isVertical ? 'mt-r-md' : 'ml-r-md',
		'max-sm:md': isVertical ? 'max-sm:mt-r-md' : 'max-sm:ml-r-md',
	};
	return classes[gutter];
}

export function getInlineNextButtonMarginClass({
	gutter,
	isVertical,
}: GutterClassGetterArgs) {
	const classes: GutterClassMap = {
		'none': undefined,
		'focus-outline': isVertical
			? 'mb-focus-outline-size'
			: 'mr-focus-outline-size',
		'md': isVertical ? 'mb-r-md' : 'mr-r-md',
		'max-sm:md': isVertical ? 'max-sm:mb-r-md' : 'max-sm:mr-r-md',
	};
	return classes[gutter];
}

// Float calculations and rounding can result in slightly off values,
// compensate with a small margin where needed.
const ROUNDING_MARGIN = 2;

/**
 * Parse a size value from a style object.
 *
 * Sizes are normalized to px even if defined as something else like rem or
 * percentage. NaN falls back to zero.
 */
export function parseSizeStyle(val: string) {
	return round(Number.parseInt(val, 10) || 0, 3);
}

/** Get DOM and CSS properties depending on the orientation. */
export function useOrientationData(orientation: Orientation) {
	const isVertical = orientation === 'vertical';
	return {
		gapProp: isVertical ? 'rowGap' : 'columnGap',
		gutterPaddingProp: isVertical ? 'paddingTop' : 'paddingLeft',
		isHorizontal: !isVertical,
		isVertical,
		scrollPosProp: isVertical ? 'scrollTop' : 'scrollLeft',
		scrollSizeProp: isVertical ? 'scrollHeight' : 'scrollWidth',
		sizeProp: isVertical ? 'height' : 'width',
		translateProp: isVertical ? 'translateY' : 'translateX',
	} as const;
}

/** Sizing data for the carousel itself. */
export function useCarouselData(orientation: Orientation) {
	const { gapProp, gutterPaddingProp, scrollSizeProp, sizeProp } =
		useOrientationData(orientation);

	const [data, setData] = useState<CarouselData>({
		containerSize: 0,
		controlButtonSize: 0,
		gapSize: 0,
		gutterSize: 0,
		snapAlignment: 'edge',
		totalSize: 0,
	});

	const updateCarouselSizeData = useCallback(
		(carouselNode: HTMLElement) => {
			const carouselRect = carouselNode.getBoundingClientRect();
			const style = getComputedStyle(carouselNode);
			const firstItem = carouselNode.querySelector('li') as HTMLElement | null;
			const firstItemStyle = firstItem
				? getComputedStyle(firstItem)
				: undefined;
			const gutterPadding = parseSizeStyle(style[gutterPaddingProp]);
			const controlButtonSize = parseSizeStyle(
				style[INLINE_CONTROLS_SIZE_PROP],
			);
			setData((state) => ({
				containerSize: round(carouselRect[sizeProp], 3),
				controlButtonSize,
				gapSize: parseSizeStyle(style[gapProp]),
				gutterSize: gutterPadding - controlButtonSize,
				snapAlignment:
					firstItemStyle?.scrollSnapAlign === 'center'
						? 'center'
						: is.oneOf(firstItemStyle?.scrollSnapAlign, 'start', 'end')
							? 'edge'
							: state.snapAlignment,
				totalSize: round(carouselNode[scrollSizeProp], 3),
			}));
		},
		[gapProp, gutterPaddingProp, scrollSizeProp, sizeProp],
	);

	return { carouselData: data, updateCarouselSizeData };
}

/**
 * State for the current scroll position.
 *
 * The state is throttled to keep the number of re-renders down. There is also
 * an unthrottled ref for animation precision.
 */
export function useCarouselScrollPosition({
	containerSize,
	throttleDelay,
}: {
	containerSize: number;
	throttleDelay: number;
}) {
	const [scrollPos, setScrollPos] = useState(0);
	const [throttledSetScrollPos, flushThrottledSetScrollPos] =
		useThrottledCallback(setScrollPos, throttleDelay);
	const scrollPosEnd = scrollPos + containerSize;
	const scrollPosRef = useRef(0);
	const wrappedSetScrollPos = (newScrollPos: number) => {
		const pos = round(newScrollPos);
		throttledSetScrollPos(pos);
		scrollPosRef.current = pos;
	};

	return {
		flushThrottledSetScrollPos,
		scrollPos,
		scrollPosEnd,
		scrollPosRef,
		setScrollPos: wrappedSetScrollPos,
	};
}

/** Sizing, positioning and visibility data for the carousel items. */
export function useCarouselItemsData({
	controlButtonSize,
	gapSize,
	gutterSize,
	itemCount,
	orientation,
	scrollPos,
	scrollPosEnd,
	snapAlignment,
}: {
	controlButtonSize: number;
	gapSize: number;
	gutterSize: number;
	itemCount: number;
	orientation: Orientation;
	scrollPos: number;
	scrollPosEnd: number;
	snapAlignment: SnapAlignment;
}) {
	const { sizeProp } = useOrientationData(orientation);
	const [itemSizes, setItemSizes] = useState<number[]>([]);
	const startPos =
		snapAlignment === 'center' ? gutterSize + controlButtonSize : 0;

	const itemsData: ItemData[] = useMemo(
		() =>
			itemSizes.map((size, i) => {
				// O(n^2) with nested loop, but it's a small set so not a problem.
				const pos =
					startPos +
					itemSizes.slice(0, i).reduce((sum, s) => sum + s + gapSize, 0);
				const posEnd = pos + size;
				// Use rounding margin to make fully visible easier and partially
				// visible harder, i.e. an item one pixel outside the container still
				// counts as fully visible while an item that has one pixel visible
				// does not yet count as partially visible.
				const isFullyVisible =
					pos >= scrollPos - ROUNDING_MARGIN &&
					posEnd <= scrollPosEnd + ROUNDING_MARGIN;

				const isPartiallyVisible =
					(pos < scrollPos - ROUNDING_MARGIN &&
						posEnd > scrollPos + ROUNDING_MARGIN) ||
					(pos < scrollPosEnd - ROUNDING_MARGIN &&
						posEnd > scrollPosEnd + ROUNDING_MARGIN);

				const visibleStart = Math.max(pos, scrollPos);
				const visibleEnd = Math.min(posEnd, scrollPosEnd);
				const visibleSize = Math.max(0, visibleEnd - visibleStart);

				return {
					index: i,
					isFullyVisible,
					isPartiallyVisible,
					isVisible: isFullyVisible || isPartiallyVisible,
					size: round(size, 3),
					pos: round(pos, 3),
					posEnd: round(posEnd, 3),
					visiblePercentage: size ? round((visibleSize / size) * 100) : 0,
				};
			}),
		[gapSize, itemSizes, scrollPos, scrollPosEnd, startPos],
	);

	const updateItemsData = useCallback(
		(itemNodes: (HTMLElement | null)[]) => {
			// Only update size if all items are passed in.
			const itemRects = itemNodes
				.filter(is.truthy)
				.map((node) => node.getBoundingClientRect());
			if (itemRects.length > 0 && itemRects.length === itemCount) {
				setItemSizes(itemRects.map((rect) => rect[sizeProp]));
			}
		},
		[itemCount, sizeProp],
	);

	return {
		hasItemsData:
			itemsData.length === itemCount && itemsData.some((item) => item.size > 0),
		itemsData,
		updateItemsData,
	};
}

export interface ScrollCarouselConfig {
	animate?: boolean;
	moveFocusOnDone?: boolean;
}

/** Animation hook wrapper specific to the carousel. */
export function useCarouselAnimation({
	animationDuration,
	onAlmostDone,
	onDone,
	setElementScrollPos,
}: {
	animationDuration: AnimationDuration;
	onAlmostDone?: (targetScrollPos: number) => void;
	onDone?: (targetScrollPos: number) => void;
	/** Function to set the scroll position on the carousel element. */
	setElementScrollPos: (pos: number) => void;
}) {
	const prefersReducedMotion = usePrefersReducedMotion();
	const duration = prefersReducedMotion
		? 0
		: ANIMATION_DURATIONS[animationDuration].time;

	const [targetScrollPos, setTargetScrollPos] = useState(0);
	const [isAnimating, setIsAnimating] = useState(false);
	const animateScroll = useAnimator({
		duration,
		onStart: (to) => {
			setTargetScrollPos(to);
			setIsAnimating(true);
		},
		onUpdate: setElementScrollPos,
		onDone: (to) => {
			setIsAnimating(false);
			onDone?.(to);
		},
	});

	const [onAlmostDoneDebounced] = useDebouncedCallback(
		(to: number) => {
			onAlmostDone?.(to);
		},
		round(duration * 0.95),
	);

	const scrollCarousel = (
		from: number,
		to: number,
		{ animate = true }: ScrollCarouselConfig = {},
	) => {
		if (from === to) {
			return;
		}
		onAlmostDoneDebounced(to);
		if (duration === 0 || !animate) {
			setElementScrollPos(round(to));
		} else {
			animateScroll(from, round(to));
		}
	};

	return {
		isAnimating,
		scrollCarousel,
		/** The target scroll position for the current animation. */
		animationTargetScrollPos: targetScrollPos,
	};
}

/** Clamp the scroll position to the container size. */
function clampScroll({
	containerSize,
	pos,
	totalSize,
}: {
	containerSize: number;
	pos: number;
	totalSize: number;
}) {
	return clamp(pos, 0, totalSize - containerSize);
}

function getItemCenterPos(item: ItemData) {
	return item.pos + (item.posEnd - item.pos) / 2;
}

/** Get the target left/top position for going backwards. */
function getPrevPageStartTargetPos({
	containerSize,
	itemsData,
	scrollPos,
}: {
	containerSize: number;
	itemsData: ItemData[];
	scrollPos: number;
}): number {
	// (See getScrollPageTargetPos note.)
	// Find the first item from the end that is not fully visible, its start edge
	// being less than the current scroll position.
	const nonVisibleItem = itemsData
		.toReversed()
		.find((item) => item.pos + ROUNDING_MARGIN < scrollPos);
	const posEndLimit = nonVisibleItem?.posEnd ?? scrollPos;
	// With the above target, find the first item that can have it fully
	// visible, its start edge and the target end edge being within the
	// container size.
	const targetItem = itemsData.find(
		(item) => item.pos + ROUNDING_MARGIN >= posEndLimit - containerSize,
	);
	return targetItem?.pos ?? scrollPos - containerSize;
}

/** Get the target left/top position for going forwards. */
function getNextPageStartTargetPos({
	containerSize,
	itemsData,
	scrollPos,
}: {
	containerSize: number;
	itemsData: ItemData[];
	scrollPos: number;
}): number {
	// (See getScrollPageTargetPos note.)
	const scrollPosEnd = scrollPos + containerSize;
	// Find the first not fully visible item, its end edge past the scroll
	// container's end edge.
	const targetItem = itemsData.find(
		(item) => item.posEnd - ROUNDING_MARGIN > scrollPosEnd,
	);
	return targetItem?.pos ?? scrollPosEnd;
}

/** Get the target center position for going backwards. */
function getPrevPageCenterTargetPos({
	containerSize,
	itemsData,
	scrollPos,
}: {
	containerSize: number;
	itemsData: ItemData[];
	scrollPos: number;
}): number {
	// (See getScrollPageTargetPos note.)
	const halfContainerSize = containerSize / 2;
	// Find the first item from the end that is not fully visible, its start edge
	// being less than the current scroll position.
	const nonVisibleItem = itemsData
		.toReversed()
		.find((item) => item.pos + ROUNDING_MARGIN < scrollPos);
	// Scroll all the way to the start if above item will be fully visible.
	// Handles case where targeting the previous item would mathematically
	// overshoot the target, but the current item target is close enough
	// to the start of the container.
	if (nonVisibleItem && nonVisibleItem.posEnd - containerSize < 0) {
		return 0;
	}
	// The target center limit is from the end edge of the above item.
	const centerLimit = (nonVisibleItem?.posEnd ?? scrollPos) - halfContainerSize;
	// Find the first item that has a center point greater than the above limit.
	const targetItem = itemsData.find(
		(item) => getItemCenterPos(item) + ROUNDING_MARGIN > centerLimit,
	);
	// Target scroll position is the left container edge from the above item's
	// center point.
	return targetItem
		? getItemCenterPos(targetItem) - halfContainerSize
		: scrollPos - halfContainerSize;
}

/** Get the target center position for going forwards. */
function getNextPageCenterTargetPos({
	containerSize,
	itemsData,
	scrollPos,
	totalSize,
}: {
	containerSize: number;
	itemsData: ItemData[];
	scrollPos: number;
	totalSize: number;
}): number {
	// (See getScrollPageTargetPos note.)
	const scrollPosEnd = scrollPos + containerSize;
	const halfContainerSize = containerSize / 2;
	// Find the first not fully visible item (end edge is greater than the end
	// edge of the container). It should be fully visible when done.
	const nonVisibleItem = itemsData.find(
		(item) => item.posEnd - ROUNDING_MARGIN > scrollPosEnd,
	);
	// Scroll all the way to the end if above item will be fully visible.
	// Handles case where targeting the next item would mathematically
	// overshoot the target, but the current item target is close enough
	// to the end of the container.
	if (nonVisibleItem && nonVisibleItem.pos + containerSize > totalSize) {
		return totalSize;
	}
	// The target center limit is from the start edge of the above item.
	const centerLimit = (nonVisibleItem?.pos ?? scrollPosEnd) + halfContainerSize;
	// Find the first item from the end that has a center point less than the
	// above limit.
	const targetItem = itemsData
		.toReversed()
		.find((item) => getItemCenterPos(item) - ROUNDING_MARGIN < centerLimit);
	// Target scroll position is the left container edge from the above item's
	// center point.
	return targetItem
		? getItemCenterPos(targetItem) - halfContainerSize
		: scrollPosEnd + halfContainerSize;
}

/**
 * Get the target left/top position for going forwards or backwards a full
 * page of items.
 */
export function getScrollPageTargetPos({
	containerSize,
	direction,
	itemsData,
	scrollPos,
	snapAlignment,
	totalSize,
}: {
	containerSize: number;
	direction: Direction;
	itemsData: ItemData[];
	scrollPos: number;
	snapAlignment: SnapAlignment;
	totalSize: number;
}): number {
	// Notes for every below function:
	// - Not using visibility flags like isPartiallyVisible since they're
	//   throttled and this can be triggered during an animation where the
	//   flags aren't updated yet.
	// - The target scroll position is the always the start (left/top) edge of
	//   the container, even for center alignment.
	// TODO: These calculations feel unnecessarily complex, it should be possible
	// to both simplify and to use the forward logic in reverse for backward.
	let pos: number;
	const args = { containerSize, itemsData, scrollPos, totalSize };
	if (snapAlignment === 'center') {
		pos =
			direction === 'next'
				? getNextPageCenterTargetPos(args)
				: getPrevPageCenterTargetPos(args);
	} else {
		pos =
			direction === 'next'
				? getNextPageStartTargetPos(args)
				: getPrevPageStartTargetPos(args);
	}
	return clampScroll({ containerSize, pos, totalSize });
}

/** Get the target left/top position for going to a specific index. */
export function getScrollIndexTargetPos({
	containerSize,
	index,
	itemsData,
	snapAlignment,
}: {
	containerSize: number;
	index: number;
	itemsData: ItemData[];
	snapAlignment: SnapAlignment;
}): number | undefined {
	const i = clampIndex(index, itemsData.length);
	const targetItem = itemsData[i];
	if (!targetItem) {
		return undefined;
	}
	const halfContainerSize = containerSize / 2;
	return snapAlignment === 'center'
		? getItemCenterPos(targetItem) - halfContainerSize
		: targetItem.pos;
}

/**
 * Get the item that could be considered the 'active' one based on the
 * scroll position.
 */
export function getScrollPosTargetItem({
	containerSize,
	itemsData,
	scrollPos,
	snapAlignment,
}: {
	containerSize: number;
	itemsData: ItemData[];
	scrollPos: number;
	snapAlignment: SnapAlignment;
}) {
	const halfContainerSize = containerSize / 2;
	const centerPos = scrollPos + halfContainerSize;
	return itemsData.find((item) =>
		snapAlignment === 'center'
			? item.pos - ROUNDING_MARGIN < centerPos &&
				item.posEnd + ROUNDING_MARGIN > centerPos
			: item.pos + ROUNDING_MARGIN > scrollPos,
	);
}
